import { cva, type VariantProps } from 'class-variance-authority';
import {
  ProgressBar as AriaProgressBar,
  type ProgressBarProps,
} from 'react-aria-components';
import styles from './LoadingSpinner.module.css';
import {
  foregroundColorVariants,
  type ForegroundVariants,
} from '../../utils/foregroundColour/foregroundColor';
import SpinnerThird from './icon/loading-spinner.svg?react';
import {
  generateResponsiveClasses,
  getBaseValueOrUndefined,
  WithResponsiveProps,
} from '../../utils/breakpoint/responsiveSSR';

const loadingSpinner = cva(styles['loading-spinner'], {
  variants: {
    buttonSize: {
      sm: styles['loading-spinner--button-sm'],
      md: styles['loading-spinner--button-md'],
      lg: styles['loading-spinner--button-lg'],
    },
    size: {
      '0.5x': styles['loading-spinner--size--0_5x'],
      '0.75x': styles['loading-spinner--size--0_75x'],
      '1x': styles['loading-spinner--size--1x'],
      '1.25x': styles['loading-spinner--size--1_25x'],
      '1.5x': styles['loading-spinner--size--1_5x'],
      '2x': styles['loading-spinner--size--2x'],
      '2.5x': styles['loading-spinner--size--2_5x'],
      '3x': styles['loading-spinner--size--3x'],
      '4x': styles['loading-spinner--size--4x'],
      '5x': styles['loading-spinner--size--5x'],
      '6x': styles['loading-spinner--size--6x'],
      '7x': styles['loading-spinner--size--7x'],
      '8x': styles['loading-spinner--size--8x'],
      '9x': styles['loading-spinner--size--9x'],
      '10x': styles['loading-spinner--size--10x'],
    },
  },
  defaultVariants: {
    size: '2x',
    buttonSize: 'md',
  },
});

type LoadingSpinnerSize = NonNullable<
  VariantProps<typeof loadingSpinner>['size']
>;

export interface LoadingSpinnerBaseProps
  extends Omit<
    ProgressBarProps,
    'value' | 'minValue' | 'maxValue' | 'children'
  > {
  /**
   * **[Responsive]** What size loading spinner
   */
  size?: LoadingSpinnerSize;
  /**
   * What size loading spinner
   * @deprecated Use size instead
   * @ignore
   */
  buttonSize?: 'sm' | 'md' | 'lg' | undefined;
  /**
   * The styleVariant of the loadingSpinner
   * @default "secondary"
   */
  styleVariant?: ForegroundVariants['foregroundColor'];
  /**
   * Aria label for the loading spinner for screen readers
   * @default "Loading"
   */
  'aria-label'?: string;
  /**
   * Additional classes to apply to the loading spinner container
   * @ignore
   */
  className?: string;
}

type ResponsiveKeys = 'size';

export type LoadingSpinnerProps = WithResponsiveProps<
  LoadingSpinnerBaseProps,
  ResponsiveKeys
>;

const LoadingSpinner = ({
  size = '2x',
  buttonSize = 'md',
  styleVariant = 'secondary',
  'aria-label': ariaLabel = 'Loading',
  className,
  ...props
}: LoadingSpinnerProps) => {
  const baseSize = getBaseValueOrUndefined(size);

  const getResponsiveClasses = generateResponsiveClasses(styles);
  const sizeResponsiveClasses = getResponsiveClasses(
    size,
    'loading-spinner--size',
  );

  return (
    <AriaProgressBar
      isIndeterminate
      className={[
        loadingSpinner({ buttonSize, size: baseSize }),
        foregroundColorVariants({ foregroundColor: styleVariant }),
        className,
        ...sizeResponsiveClasses,
      ]
        .filter(Boolean)
        .join(' ')}
      aria-valuetext={ariaLabel}
      aria-label={ariaLabel}
      data-testid="alto-loading-spinner"
      {...props}
    >
      <span
        className={[
          styles['loading-spinner__icon-wrapper'],
          loadingSpinner({ size: baseSize }),
          ...sizeResponsiveClasses,
        ]
          .filter(Boolean)
          .join(' ')}
        aria-hidden="true"
      >
        <SpinnerThird className={styles['loading-spinner__spinner']} />
      </span>
    </AriaProgressBar>
  );
};

export default LoadingSpinner;
